// Hands-free cook mode: what a spoken phrase asks for. A small fixed grammar (English and German), no model:
// the speech recognizer (on the device where the platform offers it) turns sound into text, and this turns
// text into one of a few commands. Matching is forgiving ("okay next step please" is "next").
import { fold } from './text';
import { detectTimers } from './timerText';

export type VoiceCommand =
  | { kind: 'next' }
  | { kind: 'previous' }
  | { kind: 'repeat' }
  | { kind: 'ingredients' }
  | { kind: 'goto'; step: number }
  | { kind: 'timer'; seconds: number }
  | { kind: 'stopTimers' }
  | { kind: 'howMuch'; query: string }
  | { kind: 'stopListening' };

const NUMBER_WORDS: Record<string, number> = {
  one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12, fifteen: 15, twenty: 20, thirty: 30, forty: 40, 'forty-five': 45, sixty: 60, ninety: 90,
  half: 0.5, a: 1, an: 1,
  eins: 1, ein: 1, eine: 1, einen: 1, zwei: 2, drei: 3, vier: 4, funf: 5, sechs: 6, sieben: 7, acht: 8, neun: 9, zehn: 10, elf: 11, zwolf: 12, funfzehn: 15, zwanzig: 20, dreissig: 30, 'dreißig': 30, vierzig: 40, sechzig: 60, neunzig: 90, halbe: 0.5,
};
const ORDINALS: Record<string, number> = {
  first: 1, second: 2, third: 3, fourth: 4, fifth: 5, sixth: 6, seventh: 7, eighth: 8, ninth: 9, tenth: 10, last: -1,
  erste: 1, ersten: 1, zweite: 2, zweiten: 2, dritte: 3, dritten: 3, vierte: 4, vierten: 4, funfte: 5, funften: 5, letzte: -1, letzten: -1,
};

/** Number words to digits, so "five minutes" and "fünf Minuten" read like "5 minutes". */
function digits(text: string): string {
  return text.replace(/[\p{L}-]+/gu, w => (w in NUMBER_WORDS && !['a', 'an', 'ein', 'eine', 'einen'].includes(w) ? String(NUMBER_WORDS[w]) : w))
    // "half an hour", "eine halbe Stunde", then "a minute", "eine Stunde"
    .replace(/\b(?:(?:eine|a)\s+)?0\.5\s+(?:(?:an?|eine)\s+)?(?:hour|stunde)\b/g, '30 minutes')
    .replace(/\b(?:a|an|ein|eine|einen)\s+(?=(?:minute|hour|stunde|minuten|sekunde|second))/g, '1 ');
}

const has = (t: string, ...words: string[]) => words.some(w => new RegExp(`(^|\\s)${w}(\\s|$)`).test(t));

/** The command in a transcript, or null when it is not one (cook mode then ignores it). */
export function parseVoiceCommand(transcript: string): VoiceCommand | null {
  const t = digits(fold(transcript).replace(/[^\p{L}\p{N}\s.-]/gu, ' ').replace(/\s+/g, ' ').trim());
  if (!t) return null;

  if (has(t, 'stop listening', 'stopp zuhoren', 'hor auf', 'mikrofon aus', 'microphone off')) return { kind: 'stopListening' };

  if (has(t, 'timer', 'wecker', 'stoppuhr', 'remind me', 'erinnere mich')) {
    if (has(t, 'stop', 'cancel', 'off', 'aus', 'stopp', 'abbrechen', 'loschen')) return { kind: 'stopTimers' };
    const found = detectTimers(t)[0];
    if (found) return { kind: 'timer', seconds: found.seconds };
  }

  const how = /^(?:how (?:much|many)|wie ?viel(?:e)?)\s+(?:of\s+(?:the\s+)?|do i need\s+|brauche ich\s+)?(.+?)(?:\s+(?:do i need|brauche ich|again|nochmal))?$/.exec(t);
  if (how?.[1]) return { kind: 'howMuch', query: how[1] };

  const go = /(?:go to|step|schritt|gehe zu)\s+(?:step\s+|schritt\s+)?(\d+)/.exec(t);
  if (go && !has(t, 'next', 'weiter', 'nachster')) return { kind: 'goto', step: Number(go[1]) };
  const ord = Object.keys(ORDINALS).find(w => has(t, `${w} step`, `${w}n schritt`, `${w} schritt`));
  if (ord) return { kind: 'goto', step: ORDINALS[ord] };

  if (has(t, 'ingredients', 'what do i need', 'zutaten', 'was brauche ich')) return { kind: 'ingredients' };
  if (has(t, 'repeat', 'again', 'say that again', 'read', 'wiederholen', 'nochmal', 'noch mal', 'vorlesen', 'was war das')) return { kind: 'repeat' };
  if (has(t, 'back', 'previous', 'go back', 'zuruck', 'vorherige', 'vorheriger', 'davor')) return { kind: 'previous' };
  if (has(t, 'next', 'continue', 'done', 'go on', 'forward', 'weiter', 'nachster', 'nachste', 'fertig', 'vor')) return { kind: 'next' };
  return null;
}

/** The recipe line a "how much" question is about: the step's ingredient whose name best contains the query words. */
export function answerHowMuch<T extends { title: string }>(query: string, lines: T[]): T | null {
  const q = fold(query).split(/\s+/).filter(w => w.length > 2 && !['the', 'der', 'die', 'das', 'von'].includes(w));
  let best: { line: T; score: number } | null = null;
  for (const line of lines) {
    const name = fold(line.title);
    const score = q.filter(w => name.includes(w) || name.includes(w.replace(/e?s$/, ''))).length;
    if (score > (best?.score ?? 0)) best = { line, score };
  }
  return best?.line ?? null;
}
